class MiniCart extends HTMLElement {
  constructor() {
    super();
    this.popupHideTimeout = null;
    this.popupElement = null;
    this.popupProgressContainer = null;
    this.onResizeSetPopupTopOffset = this.setPopupTopOffset.bind(this);
    this._scrollRafId = null;
    this.onScrollSetPopupTopOffset = () => {
      if (this._scrollRafId) return;
      this._scrollRafId = requestAnimationFrame(() => {
        this.setPopupTopOffset();
        this._scrollRafId = null;
      });
    };
  }

  connectedCallback() {
    this.header = document.querySelector('sticky-header');
    this.drawer = document.querySelector('cart-drawer');
    this.popupDurationMs = this.getPopupDurationMs();
    this.setPopupTopOffset();
    window.addEventListener('resize', this.onResizeSetPopupTopOffset);
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResizeSetPopupTopOffset);
    window.removeEventListener('scroll', this.onScrollSetPopupTopOffset);
  }

  getPopupDurationMs() {
    const rawValue = Number(this.dataset.popupDuration);
    if (Number.isNaN(rawValue) || rawValue <= 0) return 5000;
    return rawValue * 1000;
  }

  setPopupTopOffset() {
    const headerHidden = document.body.classList.contains('shopify-body-section-header-hidden');
    if (headerHidden) {
      const scrollspyMenu = document.querySelector('.content-scrollspy__menu');
      if (scrollspyMenu) {
        const top = Math.round(scrollspyMenu.getBoundingClientRect().bottom);
        document.documentElement.style.setProperty('--cart-add-popup-top', `${top}px`);
      } else {
        document.documentElement.style.setProperty('--cart-add-popup-top', '0px');
      }
      return;
    }
    const headerGroup = document.getElementById('shopify-section-group-header-group');
    const headerSection = document.querySelector('.shopify-section-header');
    const fallbackHeader = document.querySelector('header.header');
    const headerElement = headerGroup || headerSection || fallbackHeader;
    const top = headerElement ? Math.round(headerElement.getBoundingClientRect().bottom) : 0;
    document.documentElement.style.setProperty('--cart-add-popup-top', `${top}px`);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(html => {
        document.getElementById('mini-cart').innerHTML =
          this.getSectionInnerHTML(html, '.shopify-section');
          
          document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      })
      .catch(e => {
        console.error(e);
      });
  }

  open() {
    const detailsElement = this.drawer.querySelector('details');
    if (detailsElement.hasAttribute('open')) {
      return;
    }
    
    this.drawer.openMenuDrawer();
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      if (document.getElementById(section.id)) {
        document.getElementById(section.id).innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    }));

    this.showAddToCartPopup(parsedState);
  }

  showAddToCartPopup(parsedState) {
    this.initializeAddToCartPopup();
    if (!this.popupElement || !this.popupProgressContainer) return;

    let progressElement = document.querySelector('#mini-cart .cart-popup-progress-source .cart-popup-progress');

    if (!progressElement && parsedState?.sections?.['mini-cart']) {
      const parsedMiniCart = new DOMParser().parseFromString(parsedState.sections['mini-cart'], 'text/html');
      progressElement = parsedMiniCart.querySelector('.cart-popup-progress');
    }

    this.popupProgressContainer.innerHTML = '';

    if (progressElement) {
      this.popupProgressContainer.appendChild(progressElement.cloneNode(true));
    }

    this.setPopupTopOffset();
    this.popupElement.classList.add('is-visible');
    window.addEventListener('scroll', this.onScrollSetPopupTopOffset, { passive: true });

    if (this.popupHideTimeout) {
      clearTimeout(this.popupHideTimeout);
    }

    this.popupHideTimeout = window.setTimeout(() => {
      this.hideAddToCartPopup();
    }, this.popupDurationMs || 5000);
  }

  hideAddToCartPopup() {
    if (!this.popupElement) return;
    this.popupElement.classList.remove('is-visible');
    window.removeEventListener('scroll', this.onScrollSetPopupTopOffset);

    if (this.popupHideTimeout) {
      clearTimeout(this.popupHideTimeout);
      this.popupHideTimeout = null;
    }
  }

  initializeAddToCartPopup() {
    if (this.popupElement) return;

    const popup = document.createElement('div');
    popup.className = 'cart-add-popup';
    popup.innerHTML = `
      <div class="cart-add-popup__inner">
        <div class="cart-add-popup__left">
          <div class="cart-add-popup__title">
          <div class="cart-add-popup__icon_wrapper">
            <span class="cart-add-popup__icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="9" cy="9" r="9" fill="#22C259"></circle>
                <path d="M11.7857 7L7.59286 11L5.5 9" stroke="white" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </span>
            <span>Item added to your cart!</span>
            </div>
            <div class="cart-add-popup__right">
              <button type="button" class="cart-add-popup__view-cart">View Cart</button>
              <button type="button" class="cart-add-popup__close" aria-label="Close notification">&times;</button>
            </div>
          </div>
          <div class="cart-add-popup__progress"></div>
        </div>
      </div>
    `;

    const closeButton = popup.querySelector('.cart-add-popup__close');
    const viewCartButton = popup.querySelector('.cart-add-popup__view-cart');
    this.popupProgressContainer = popup.querySelector('.cart-add-popup__progress');

    closeButton?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.hideAddToCartPopup();
    });

    viewCartButton?.addEventListener('click', () => {
      this.hideAddToCartPopup();
      this.open();
    });

    popup.addEventListener('click', (event) => {
      if (event.target.closest('.cart-add-popup__view-cart')) return;
      this.hideAddToCartPopup();
      this.open();
    });

    document.body.appendChild(popup);
    this.popupElement = popup;
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('mini-cart', MiniCart);
